/**
 * @fileoverview Phone Number Service
 * 
 * Enhanced service layer for phone number operations with Twilio and billing integration.
 * Provides methods for searching, purchasing, and managing phone numbers with proper
 * error handling and billing validation.
 */

import { PhoneNumberContext } from '../types.js';

/**
 * Phone Number Service Class
 * Handles all phone number operations with enhanced error handling and billing integration
 */
export class PhoneNumberService {
  constructor(apiClient, context) {
    this.apiClient = apiClient;
    this.context = context;
  }

  /**
   * Search for available phone numbers with Twilio integration
   * @param {Object} searchCriteria - Search criteria
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchPhoneNumbers(searchCriteria, options = {}) {
    const { context = PhoneNumberContext.USER, maxResults = 20, timeout = 15000 } = options;

    // Build search parameters with proper Twilio parameter mapping
    const params = new URLSearchParams();
    
    // Map frontend search criteria to backend API parameters
    if (searchCriteria.country) {
      params.append('countryCode', searchCriteria.country);
    }
    if (searchCriteria.areaCode) {
      params.append('areaCode', searchCriteria.areaCode);
    }
    if (searchCriteria.city) {
      params.append('city', searchCriteria.city);
    }
    if (searchCriteria.region) {
      params.append('region', searchCriteria.region);
    }
    if (searchCriteria.postalCode) {
      params.append('postalCode', searchCriteria.postalCode);
    }
    if (searchCriteria.phoneExchange) {
      params.append('phoneExchange', searchCriteria.phoneExchange);
    }
    if (searchCriteria.numberPattern) {
      params.append('numberPattern', searchCriteria.numberPattern);
    }
    if (searchCriteria.letterPattern) {
      params.append('letterPattern', searchCriteria.letterPattern);
    }
    if (searchCriteria.capabilities && Array.isArray(searchCriteria.capabilities)) {
      params.append('capabilities', searchCriteria.capabilities.join(','));
    }

    // Add result limit
    if (maxResults) {
      params.append('limit', maxResults.toString());
    }

    // Determine API endpoint based on context
    const endpoint = context === PhoneNumberContext.ADMIN 
      ? '/api/admin/phone-numbers/search'
      : '/api/phone/search';

    try {
      const response = await this.apiClient.get(
        `${endpoint}?${params}`,
        { 
          timeout,
          requestType: 'phone-search',
          cache: true, // Enable caching for search results
          cacheTtl: 5 * 60 * 1000 // 5 minutes cache
        }
      );

      return {
        success: true,
        results: response.results || [],
        count: response.count || 0,
        searchCriteria: response.searchCriteria || searchCriteria,
        searchType: response.searchType || 'general',
        cached: response.cached || false,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Phone number search failed:', error);
      
      return {
        success: false,
        results: [],
        count: 0,
        error: this.formatSearchError(error),
        errorCode: error.status || 'UNKNOWN',
        errorType: error.category || 'network',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Validate phone number purchase eligibility
   * @param {string} phoneNumber - Phone number to validate
   * @param {Object} options - Validation options
   * @returns {Promise<Object>} Validation result
   */
  async validatePurchase(phoneNumber, options = {}) {
    const { context = PhoneNumberContext.USER, timeout = 10000 } = options;

    try {
      const endpoint = context === PhoneNumberContext.ADMIN
        ? '/api/admin/phone-numbers/purchase/validate'
        : '/api/phone/purchase/validate';

      const response = await this.apiClient.post(endpoint, {
        phoneNumber
      }, {
        timeout,
        requestType: 'phone-validation'
      });

      return {
        success: true,
        canPurchase: response.canPurchase || false,
        errors: response.errors || [],
        warnings: response.warnings || [],
        costInfo: response.costInfo || null,
        billingContext: response.billingContext || null,
        requiresPayment: response.requiresPayment || false,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Purchase validation failed:', error);
      
      return {
        success: false,
        canPurchase: false,
        errors: [this.formatPurchaseError(error)],
        warnings: [],
        costInfo: null,
        billingContext: null,
        requiresPayment: false,
        errorCode: error.status || 'UNKNOWN',
        errorType: error.category || 'network',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Purchase a phone number with Twilio and billing integration
   * @param {string} phoneNumber - Phone number to purchase
   * @param {Object} options - Purchase options
   * @returns {Promise<Object>} Purchase result
   */
  async purchasePhoneNumber(phoneNumber, options = {}) {
    const { 
      context = PhoneNumberContext.USER, 
      billingContext = null,
      metadata = {},
      timeout = 30000 
    } = options;

    // Prepare purchase request
    const purchaseRequest = {
      phoneNumber,
      billingContext,
      metadata: {
        context,
        moduleVersion: '1.0.0',
        timestamp: new Date().toISOString(),
        ...metadata
      }
    };

    // Determine API endpoint based on context
    const endpoint = context === PhoneNumberContext.ADMIN
      ? '/api/admin/phone-numbers/purchase'
      : '/api/phone/purchase';

    try {
      const response = await this.apiClient.post(endpoint, purchaseRequest, {
        timeout,
        requestType: 'phone-purchase'
      });

      return {
        success: true,
        id: response.id,
        number: response.number || phoneNumber,
        friendlyName: response.friendlyName,
        billing: response.billing || null,
        twilioId: response.id,
        purchaseTimestamp: new Date().toISOString(),
        ...response
      };

    } catch (error) {
      console.error('Phone number purchase failed:', error);
      
      return {
        success: false,
        error: this.formatPurchaseError(error),
        errorCode: error.status || 'UNKNOWN',
        errorType: error.category || 'network',
        billingContext: error.billingContext || null,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get user's billing context for phone number purchases
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Billing context
   */
  async getBillingContext(options = {}) {
    const { timeout = 10000 } = options;

    try {
      const response = await this.apiClient.get('/api/phone/billing-context', {
        timeout,
        requestType: 'billing-context',
        cache: true,
        cacheTtl: 2 * 60 * 1000 // 2 minutes cache
      });

      return {
        success: true,
        billingContext: response.billingContext,
        hasValidBilling: response.hasValidBilling || false,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Failed to get billing context:', error);
      
      return {
        success: false,
        billingContext: null,
        hasValidBilling: false,
        error: this.formatBillingError(error),
        errorCode: error.status || 'UNKNOWN',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get phone number search history
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Search history
   */
  async getSearchHistory(options = {}) {
    const { limit = 10, offset = 0, timeout = 10000 } = options;

    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });

    try {
      const response = await this.apiClient.get(`/api/phone/search/history?${params}`, {
        timeout,
        requestType: 'search-history',
        cache: true,
        cacheTtl: 1 * 60 * 1000 // 1 minute cache
      });

      return {
        success: true,
        history: response.history || [],
        total: response.total || 0,
        limit: response.limit || limit,
        offset: response.offset || offset,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Failed to get search history:', error);
      
      return {
        success: false,
        history: [],
        total: 0,
        limit,
        offset,
        error: this.formatSearchError(error),
        errorCode: error.status || 'UNKNOWN',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Format search error messages
   * @param {Error} error - Error object
   * @returns {string} Formatted error message
   */
  formatSearchError(error) {
    if (error.status === 502) {
      return 'Phone number search service is temporarily unavailable. Please try again.';
    } else if (error.status === 429) {
      return 'Too many search requests. Please wait a moment before searching again.';
    } else if (error.status === 401) {
      return 'Authentication required. Please sign in again.';
    } else if (error.status === 403) {
      return 'You do not have permission to search for phone numbers.';
    } else if (error.message) {
      return error.message;
    }
    return 'Failed to search for phone numbers. Please try again.';
  }

  /**
   * Format purchase error messages
   * @param {Error} error - Error object
   * @returns {string} Formatted error message
   */
  formatPurchaseError(error) {
    if (error.status === 502) {
      return 'Phone number purchase service is temporarily unavailable. Please try again.';
    } else if (error.status === 409) {
      return 'This phone number is no longer available. Please select a different number.';
    } else if (error.status === 402) {
      return 'Insufficient funds. Please add money to your account or upgrade your plan.';
    } else if (error.status === 429) {
      return 'Too many purchase requests. Please wait a moment before trying again.';
    } else if (error.status === 401) {
      return 'Authentication required. Please sign in again.';
    } else if (error.status === 403) {
      return 'You do not have permission to purchase phone numbers.';
    } else if (error.message) {
      return error.message;
    }
    return 'Failed to purchase phone number. Please try again.';
  }

  /**
   * Format billing error messages
   * @param {Error} error - Error object
   * @returns {string} Formatted error message
   */
  formatBillingError(error) {
    if (error.status === 402) {
      return 'Billing setup required. Please configure your payment method.';
    } else if (error.status === 401) {
      return 'Authentication required. Please sign in again.';
    } else if (error.status === 403) {
      return 'You do not have permission to access billing information.';
    } else if (error.message) {
      return error.message;
    }
    return 'Failed to retrieve billing information. Please try again.';
  }
}

/**
 * Create a phone number service instance
 * @param {Object} apiClient - API client instance
 * @param {Object} context - Module context
 * @returns {PhoneNumberService} Service instance
 */
export function createPhoneNumberService(apiClient, context) {
  return new PhoneNumberService(apiClient, context);
}

export default PhoneNumberService;
